import { ArrowLeft } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { StatusBadge } from "../components/common/StatusBadge";
import { SummaryCard } from "../components/common/SummaryCard";
import { extractErrorMessage, fetchCustomerDetail } from "../lib/api";
import { getVisibleLoadStatus } from "../lib/statuses";

const formatMoney = (value, currency) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency, maximumFractionDigits: 2 }).format(value || 0);

export const CustomerDetailPage = () => {
  const { customerId } = useParams();
  const [searchParams] = useSearchParams();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCustomer = async () => {
      try {
        const payload = await fetchCustomerDetail(customerId);
        setDetail(payload);
      } catch (error) {
        toast.error(extractErrorMessage(error, "Unable to load this customer."));
      } finally {
        setLoading(false);
      }
    };

    loadCustomer();
  }, [customerId]);

  const customerSearch = searchParams.get("q") || "";
  const filteredLoads = useMemo(() => {
    if (!detail?.loads) {
      return [];
    }
    if (!customerSearch) {
      return detail.loads;
    }
    const query = customerSearch.toLowerCase();
    return detail.loads.filter((load) =>
      [load.load_number, load.pickup_company, load.delivery_company, load.pickup_city, load.delivery_city, getVisibleLoadStatus(load)].some((field) =>
        String(field || "").toLowerCase().includes(query),
      ),
    );
  }, [customerSearch, detail]);

  const customer = detail?.customer;

  return (
    <div className="space-y-6" data-testid="customer-detail-page">
      <section className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <Link
            className="inline-flex items-center gap-2 text-sm font-semibold text-zinc-600 transition hover:text-zinc-900"
            data-testid="customer-detail-back-link"
            to="/customers"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to customers
          </Link>
          <p className="mt-4 text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Customer overview</p>
          <h1 className="mt-2 text-4xl font-semibold text-zinc-900">{loading ? "Loading customer..." : customer?.customer_name}</h1>
        </div>
      </section>

      <section className="grid gap-4 xl:grid-cols-[1.35fr_0.65fr]">
        <div className="panel-surface px-5 py-5" data-testid="customer-detail-info-card">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Customer information</p>
          {loading ? (
            <p className="mt-6 text-sm text-zinc-500">Loading customer information...</p>
          ) : (
            <div className="mt-5 grid gap-5 md:grid-cols-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500">Contact</p>
                <p className="mt-2 text-lg font-semibold text-zinc-900" data-testid="customer-detail-contact-name">{customer.contact_name}</p>
                <p className="mt-1 text-sm text-zinc-500" data-testid="customer-detail-phone">{customer.phone}</p>
                <p className="mt-1 text-sm text-zinc-500" data-testid="customer-detail-email">{customer.email}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500">Billing</p>
                <p className="mt-2 text-sm text-zinc-700" data-testid="customer-detail-billing-address">{customer.billing_address}</p>
                <p className="mt-3 text-sm font-semibold text-zinc-900" data-testid="customer-detail-default-currency">Default currency: {customer.default_currency}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500">Notes</p>
                <p className="mt-2 text-sm text-zinc-600" data-testid="customer-detail-notes">{customer.notes || "No notes added yet."}</p>
              </div>
            </div>
          )}
        </div>

        <div className="panel-surface space-y-4 px-5 py-5" data-testid="customer-status-breakdown-card">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Loads by status</p>
            <p className="mt-2 text-sm text-zinc-500">Breakdown for this customer only.</p>
          </div>
          <div className="space-y-3">
            {loading ? (
              <p className="text-sm text-zinc-500">Loading status totals...</p>
            ) : (
              detail.loads_by_status.map((statusRow) => (
                <div className="flex items-center justify-between rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3" data-testid={`customer-status-row-${statusRow.status}`} key={statusRow.status}>
                  <StatusBadge status={statusRow.status} testId={`customer-status-badge-${statusRow.status.replace(/\s+/g, "-").toLowerCase()}`} />
                  <span className="text-sm font-semibold text-zinc-900">{statusRow.count}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-3">
        <SummaryCard accent="zinc" helperText="Loads for this customer only." label="Total loads" testId="customer-total-loads-card" value={loading ? "..." : detail?.total_loads || 0} />
        <SummaryCard accent="green" helperText="CAD revenue remains separate." label="CAD revenue" testId="customer-cad-revenue-card" value={loading ? "..." : formatMoney(detail?.total_cad_revenue, "CAD")} />
        <SummaryCard accent="blue" helperText="USD revenue remains separate." label="USD revenue" testId="customer-usd-revenue-card" value={loading ? "..." : formatMoney(detail?.total_usd_revenue, "USD")} />
      </section>

      <section className="panel-surface px-5 py-5" data-testid="customer-delivered-not-invoiced-card">
        <h2 className="text-2xl font-semibold text-zinc-900">Delivered but not invoiced</h2>
        <div className="mt-4 space-y-3">
          {(detail?.delivered_not_invoiced_loads || []).map((load) => (
            <div className="rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3" data-testid={`customer-delivered-not-invoiced-${load.id}`} key={load.id}>
              <p className="font-semibold text-zinc-900">{load.load_number}</p>
              <p className="mt-1 text-sm text-zinc-500">{load.delivery_company} — {load.delivery_city}</p>
            </div>
          ))}
          {!loading && !(detail?.delivered_not_invoiced_loads || []).length ? <p className="text-sm text-zinc-500">No delivered loads are waiting for invoicing.</p> : null}
        </div>
      </section>

      <section className="table-shell" data-testid="customer-loads-table-section">
        <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-4">
          <div>
            <h2 className="text-2xl font-semibold text-zinc-900">Customer loads</h2>
          </div>
          <span className="rounded-full border border-zinc-200 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500" data-testid="customer-loads-count-pill">
            {filteredLoads.length} shown
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="data-table" data-testid="customer-loads-table">
            <thead>
              <tr>
                <th>Load</th>
                <th>Pickup</th>
                <th>Delivery</th>
                <th>Assignment</th>
                <th>Status</th>
                <th>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="py-10 text-center text-sm text-zinc-500" colSpan={6} data-testid="customer-loads-loading-state">
                    Loading loads...
                  </td>
                </tr>
              ) : filteredLoads.length ? (
                filteredLoads.map((load) => (
                  <tr key={load.id} data-testid={`customer-load-row-${load.id}`}>
                    <td>
                      <p className="font-semibold text-zinc-900">{load.load_number}</p>
                    </td>
                    <td>
                      <p>{load.pickup_company}</p>
                      <p className="mt-1 text-xs text-zinc-500">{load.pickup_address}</p>
                    </td>
                    <td>
                      <p>{load.delivery_company}</p>
                      <p className="mt-1 text-xs text-zinc-500">{load.delivery_address}</p>
                    </td>
                    <td>
                      <p>{load.truck_number || "No truck"}</p>
                      <p className="mt-1 text-xs text-zinc-500">{load.driver_name || "No driver"}</p>
                    </td>
                    <td>
                      <StatusBadge status={getVisibleLoadStatus(load)} testId={`customer-load-status-${load.id}`} />
                    </td>
                    <td>{formatMoney(load.rate, load.currency)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="py-10 text-center text-sm text-zinc-500" colSpan={6} data-testid="customer-loads-empty-state">
                    No loads match your current search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};